import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { curationApi, proposalApi, rejectionApi, justificationApi } from '../services/api';
import type {
  CurationChunk,
  CurationDocument,
  CurationIndex,
  JustificationRecord,
  ProposalSession,
  RejectionRecord,
  SessionTree,
} from '../types';
import './ContentPanel.css';

const TEXT_FILE_EXTENSIONS = new Set([
  '.txt',
  '.md',
  '.markdown',
  '.csv',
  '.tsv',
  '.json',
  '.yaml',
  '.yml',
  '.xml',
  '.html',
  '.htm',
  '.css',
  '.js',
  '.ts',
  '.tsx',
  '.jsx',
  '.py',
  '.go',
  '.java',
  '.cs',
  '.sql',
  '.sh',
  '.ps1',
  '.bat',
  '.cmd',
  '.log',
  '.ini',
  '.cfg',
  '.conf',
  '.properties',
  '.env',
  '.rtf',
]);

const TEXT_MIME_PREFIXES = ['text/'];
const TEXT_MIME_TYPES = new Set([
  'application/json',
  'application/xml',
  'application/x-yaml',
  'application/yaml',
  'application/javascript',
  'application/typescript',
  'application/x-sh',
]);

const RESET_POLL_ATTEMPTS = 6;
const RESET_POLL_DELAY_MS = 350;

function flattenSessions(tree: SessionTree | null): ProposalSession[] {
  if (!tree) {
    return [];
  }

  const current = tree.session && tree.session.sessionId !== 'live' ? [tree.session] : [];
  return current.concat(...(tree.branches ?? []).map((branch) => flattenSessions(branch)));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : '';
}

function getDefaultTitleFromFileName(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return (lastDot > 0 ? fileName.slice(0, lastDot) : fileName).trim();
}

function isAcceptedTextFile(file: File): boolean {
  const extension = getFileExtension(file.name);
  if (TEXT_FILE_EXTENSIONS.has(extension)) {
    return true;
  }

  const mimeType = file.type.toLowerCase();
  if (TEXT_MIME_TYPES.has(mimeType)) {
    return true;
  }

  return TEXT_MIME_PREFIXES.some((prefix) => mimeType.startsWith(prefix));
}

interface ContentPanelProps {
  currentUserSubject?: string | null;
  onSessionSubmitted?: () => void;
  onSelectSession?: (sessionId: string) => void;
  onContentTargetChange?: (
    target: { indexName: string; documentId: string; chunk: string | null } | null
  ) => void;
  initialIndex?: string;
  initialDocumentId?: string;
  initialChunk?: string | null;
}

export interface ContentPanelHandle {
  refreshSelectedIndex: () => Promise<void>;
}

const ContentPanel = React.forwardRef<ContentPanelHandle, ContentPanelProps>(
  (
    {
      currentUserSubject,
      onSessionSubmitted,
      onSelectSession,
      onContentTargetChange,
      initialIndex,
      initialDocumentId,
      initialChunk,
    },
    ref
  ) => {
    const [indexes, setIndexes] = useState<CurationIndex[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<string>('');
    const [documents, setDocuments] = useState<CurationDocument[]>([]);
    const [expandedDocumentIds, setExpandedDocumentIds] = useState<Record<string, boolean>>({});
    const [showFreeformForm, setShowFreeformForm] = useState<boolean>(false);
    const [freeformTitle, setFreeformTitle] = useState<string>('');
    const [freeformContent, setFreeformContent] = useState<string>('');
    const [isFreeformDropActive, setIsFreeformDropActive] = useState<boolean>(false);
    const [loadingIndexes, setLoadingIndexes] = useState<boolean>(false);
    const [loadingDocuments, setLoadingDocuments] = useState<boolean>(false);
    const [analyzingChunkIds, setAnalyzingChunkIds] = useState<Set<string>>(new Set());
    const [resettingChunkId, setResettingChunkId] = useState<string>('');
    const [addingFreeform, setAddingFreeform] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [resetClearRejectionsByChunk, setResetClearRejectionsByChunk] = useState<
      Record<string, boolean>
    >({});
    const [lastAgentSession, setLastAgentSession] = useState<ProposalSession | null>(null);
    const [expandedStatuses, setExpandedStatuses] = useState<Record<string, boolean>>({});
    const [focusedDocumentID, setFocusedDocumentID] = useState<string>('');
    const [focusedChunkToken, setFocusedChunkToken] = useState<string | null>(null);
    const [rejectionHistoryByDocument, setRejectionHistoryByDocument] = useState<
      Record<string, RejectionRecord[]>
    >({});
    const [justificationHistoryByDocument, setJustificationHistoryByDocument] = useState<
      Record<string, JustificationRecord[]>
    >({});
    const handledDeepLinkKeyRef = useRef<string>('');
    const freeformDragDepthRef = useRef<number>(0);
    const selectedIndexConfig = useMemo(() => {
      if (!selectedIndex) {
        return null;
      }
      return indexes.find((idx) => idx.name === selectedIndex) ?? null;
    }, [indexes, selectedIndex]);
    const selectedIndexAllowsUploads = selectedIndexConfig?.allow_uploads === true;

    useEffect(() => {
      let active = true;
      const loadIndexes = async (): Promise<void> => {
        setLoadingIndexes(true);
        setMessage('');
        try {
          const rows = await curationApi.listIndexes();
          if (!active) return;
          setIndexes(rows);
          if (initialIndex && rows.some((row) => row.name === initialIndex)) {
            setSelectedIndex(initialIndex);
          } else if (rows.length > 0) {
            setSelectedIndex(rows[0].name);
          }
        } catch (err) {
          if (!active) return;
        } finally {
          if (active) setLoadingIndexes(false);
        }
      };

      loadIndexes();
      return () => {
        active = false;
      };
    }, [initialIndex]);

    const loadDocuments = useCallback(
      async (indexName: string, preserveMessage = false): Promise<void> => {
        if (!indexName) {
          setDocuments([]);
          setExpandedDocumentIds({});
          return;
        }

        setLoadingDocuments(true);
        if (!preserveMessage) {
          setMessage('');
        }

        try {
          const rows = await curationApi.listDocuments(indexName, 100);
          setDocuments(rows);
          setExpandedDocumentIds((prev) => {
            const next: Record<string, boolean> = {};
            rows.forEach((doc) => {
              if (prev[doc.id]) {
                next[doc.id] = true;
              }
            });
            return next;
          });
        } catch (err) {
          setMessage(`Failed to load documents: ${(err as Error).message}`);
        } finally {
          setLoadingDocuments(false);
        }
      },
      []
    );

    const waitForChunkReset = useCallback(
      async (indexName: string, documentId: string, chunkId: string): Promise<boolean> => {
        for (let attempt = 0; attempt < RESET_POLL_ATTEMPTS; attempt += 1) {
          const rows = await curationApi.listDocuments(indexName, 100);
          setDocuments(rows);
          setExpandedDocumentIds((prev) => {
            const next: Record<string, boolean> = {};
            rows.forEach((doc) => {
              if (prev[doc.id]) {
                next[doc.id] = true;
              }
            });
            return next;
          });

          const refreshedDoc = rows.find((row) => row.id === documentId);
          const refreshedChunk = refreshedDoc?.chunks?.find((row) => row.id === chunkId);
          const normalizedStatus = (refreshedChunk?.evaluation_status || '').trim().toLowerCase();
          const hasNoEvaluationDetails = (refreshedChunk?.evaluation_details?.length ?? 0) === 0;
          if (refreshedChunk && normalizedStatus === '' && hasNoEvaluationDetails) {
            return true;
          }

          if (attempt < RESET_POLL_ATTEMPTS - 1) {
            await delay(RESET_POLL_DELAY_MS);
          }
        }

        return false;
      },
      []
    );

    useEffect(() => {
      if (!selectedIndex) {
        setDocuments([]);
        setExpandedDocumentIds({});
        return;
      }

      void loadDocuments(selectedIndex);
    }, [selectedIndex, loadDocuments]);

    useEffect(() => {
      if (!selectedIndex) {
        onContentTargetChange?.(null);
        return;
      }
      // Prefer the currently focused document (from a deep link or a chunk click)
      // so its chunk id is preserved in the URL. Fall back to whichever document
      // the user has expanded manually.
      const expandedDocumentId =
        Object.entries(expandedDocumentIds).find(([, expanded]) => expanded)?.[0] ?? '';
      const documentId = focusedDocumentID || expandedDocumentId;
      const chunk = documentId && focusedDocumentID === documentId ? focusedChunkToken : null;
      onContentTargetChange?.({
        indexName: selectedIndex,
        documentId,
        chunk,
      });
    }, [
      expandedDocumentIds,
      focusedChunkToken,
      focusedDocumentID,
      onContentTargetChange,
      selectedIndex,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        refreshSelectedIndex: async () => {
          if (!selectedIndex) {
            setMessage('Select an index to refresh documents.');
            return;
          }
          await loadDocuments(selectedIndex, true);
        },
      }),
      [loadDocuments, selectedIndex]
    );

    useEffect(() => {
      if (!initialIndex || !initialDocumentId || !selectedIndex) return;
      if (selectedIndex !== initialIndex) return;

      const key = `${initialIndex}|${initialDocumentId}|${initialChunk ?? ''}`;
      if (handledDeepLinkKeyRef.current === key) return;

      let active = true;
      const ensureTargetDocument = async (): Promise<void> => {
        try {
          let target = documents.find((doc) => doc.id === initialDocumentId) ?? null;
          if (!target) {
            target = await curationApi.getDocument(initialIndex, initialDocumentId);
            if (!active) return;
            setDocuments((prev) => {
              const existing = prev.find((doc) => doc.id === target!.id);
              if (existing) {
                return prev.map((doc) => (doc.id === target!.id ? target! : doc));
              }
              return [target!, ...prev];
            });
          }

          if (!active) return;
          handledDeepLinkKeyRef.current = key;
          setExpandedDocumentIds((prev) => ({ ...prev, [initialDocumentId]: true }));
          setFocusedDocumentID(initialDocumentId);
          setFocusedChunkToken(initialChunk ?? null);
        } catch (err) {
          if (active) {
            setMessage(`Failed to open source document: ${(err as Error).message}`);
          }
        }
      };

      void ensureTargetDocument();
      return () => {
        active = false;
      };
    }, [documents, initialChunk, initialDocumentId, initialIndex, selectedIndex]);

    useEffect(() => {
      if (!focusedDocumentID) return;

      let targetElement: HTMLElement | null = null;
      if (focusedChunkToken) {
        const chunkCards = Array.from(
          document.querySelectorAll<HTMLElement>('.content-panel__chunk-card')
        );
        targetElement =
          chunkCards.find((card) => {
            const sameDoc = card.dataset.docId === focusedDocumentID;
            if (!sameDoc) return false;
            return card.dataset.chunkId === focusedChunkToken;
          }) ?? null;
      }

      if (!targetElement) {
        targetElement = document.getElementById(`doc-card-${focusedDocumentID}`);
      }

      if (targetElement) {
        const el = targetElement;
        requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }, [documents, focusedChunkToken, focusedDocumentID]);

    useEffect(() => {
      let active = true;

      const loadChunkHistories = async (): Promise<void> => {
        for (const doc of documents) {
          if (!expandedDocumentIds[doc.id]) continue;
          if (rejectionHistoryByDocument[doc.id] === undefined) {
            try {
              const rows = await rejectionApi.getBySourceDocument(doc.id);
              if (!active) return;
              setRejectionHistoryByDocument((prev) => ({ ...prev, [doc.id]: rows }));
            } catch {
              if (!active) return;
              // Avoid repeated retries while user is browsing this document.
              setRejectionHistoryByDocument((prev) => ({ ...prev, [doc.id]: [] }));
            }
          }

          if (justificationHistoryByDocument[doc.id] === undefined) {
            try {
              const rows = await justificationApi.getBySourceDocument(doc.id);
              if (!active) return;
              setJustificationHistoryByDocument((prev) => ({ ...prev, [doc.id]: rows }));
            } catch {
              if (!active) return;
              // Avoid repeated retries while user is browsing this document.
              setJustificationHistoryByDocument((prev) => ({ ...prev, [doc.id]: [] }));
            }
          }
        }
      };

      void loadChunkHistories();
      return () => {
        active = false;
      };
    }, [
      documents,
      expandedDocumentIds,
      rejectionHistoryByDocument,
      justificationHistoryByDocument,
    ]);

    const loadLastAgentSession = useCallback(async (): Promise<void> => {
      const liveSession = await proposalApi.listSessions();
      const sessions = flattenSessions(liveSession);
      const candidate =
        sessions.find((session) => {
          if (!session.agentAuthor) return false;
          if ((session.status || '').toLowerCase() !== 'submitted') return false;
          if (currentUserSubject && session.initiatingHumanSubject) {
            return session.initiatingHumanSubject === currentUserSubject;
          }
          return true;
        }) ?? null;
      setLastAgentSession(candidate);
    }, [currentUserSubject]);

    useEffect(() => {
      void loadLastAgentSession();
    }, [loadLastAgentSession]);

    const waitForSubmission = async (activeSessionId: string): Promise<void> => {
      // Poll for analysis completion with improved status tracking
      // The backend now returns 202 Accepted with status="analyzing"
      // Polls for: analyzing → submitted (success) or error (failure)
      const maxPolls = 150; // 2.5 min max (150 * 1s)
      const pollInterval = 1000; // 1 second
      let previousStatus = '';
      let previousAnalysisStatus = '';

      for (let attempt = 0; attempt < maxPolls; attempt += 1) {
        const session = await proposalApi.getSession(activeSessionId);
        const status = (session.status || '').toLowerCase();
        const analysisStatus = (session.analysisStatus || '').toLowerCase();
        const errorDetail = session.errorDetail?.trim() || '';

        // Track status changes for logging
        if (status !== previousStatus) {
          slog('info', `Session ${activeSessionId}: status=${status}`);
          previousStatus = status;
        }
        if (analysisStatus !== previousAnalysisStatus) {
          slog('debug', `Session ${activeSessionId}: analysisStatus=${analysisStatus}`);
          previousAnalysisStatus = analysisStatus;
        }

        // Terminal states: submitted (success) or error (failure)
        if (status === 'submitted') {
          // Success: analysis complete, proposals submitted
          const proposalCount = session.stepGroups?.length || 0;
          const successMsg =
            proposalCount > 0
              ? `✓ Analysis complete, ${proposalCount} proposal${proposalCount !== 1 ? 's' : ''} found`
              : '✓ Analysis complete, no changes proposed';
          setMessage(successMsg);
          onSessionSubmitted?.();
          return;
        }
        if (status === 'error') {
          // Failure: analysis failed
          const errorMsg = errorDetail
            ? `⚠ Analysis failed: ${errorDetail}`
            : '⚠ Analysis failed: Check document quality or try again';
          setMessage(errorMsg);
          onSessionSubmitted?.();
          return;
        }

        // In-progress: show analyzing status
        if (status === 'open') {
          if (analysisStatus === 'analyzing' || analysisStatus === 'running') {
            const elapsed = Math.floor(attempt / 2); // Rough elapsed seconds
            setMessage(`⟳ Analyzing...${elapsed > 10 ? ` (${elapsed}s)` : ''}`);
          } else if (analysisStatus === 'queued') {
            setMessage('⟳ Queued for analysis...');
          } else if (analysisStatus === 'completed') {
            setMessage('⟳ Processing proposals...');
          }
        }

        // Not done yet, wait and retry
        if (attempt < maxPolls - 1) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }
      }

      // Timeout: max polling duration exceeded
      setMessage('⚠ Analysis took too long (2.5 min timeout). Try again or contact support.');
      onSessionSubmitted?.();
    };

    // Simple slog utility for frontend
    const slog = (level: 'debug' | 'info' | 'warn' | 'error', msg: string): void => {
      if (typeof console !== 'undefined') {
        console[level === 'debug' ? 'debug' : level === 'info' ? 'log' : level](`[Dewey] ${msg}`);
      }
    };

    const analyzeChunk = async (doc: CurationDocument, chunk: CurationChunk): Promise<void> => {
      if (analyzingChunkIds.has(chunk.id)) return;
      setAnalyzingChunkIds((current) => new Set(current).add(chunk.id));
      setMessage('');
      try {
        const requestedSessionID =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const docTitle = (doc.title || doc.filename || doc.id || 'Untitled document').trim();
        const sessionTitle = `${docTitle} - chunk ${chunk.id}`;
        const created = await proposalApi.createSession({
          session_id: requestedSessionID,
          title: sessionTitle,
          depends_upon_session: lastAgentSession?.sessionId || 'live',
        });
        const activeSessionId = created.sessionId;

        // Submit analysis request (backend returns 202 Accepted)
        const res = await curationApi.analyzeSession(activeSessionId, {
          source_type: 'document',
          index_name: doc.index_name,
          document_id: doc.id,
          chunk_id: chunk.id,
          run_id: doc.run_id,
          title: doc.title,
          filename: doc.filename,
          uri: doc.uri,
          source: doc.source,
          chunk_text: chunk.content,
        });

        // Handle response status
        const responseStatus = (res.status || '').toLowerCase();
        if (responseStatus === 'no_changes') {
          // No analysis needed
          setMessage('✓ Analysis completed with no proposed changes for this chunk.');
        } else if (responseStatus === 'analyzing') {
          // Async job accepted, now poll for completion
          setMessage('⟳ Analysis in progress...');
          await waitForSubmission(activeSessionId);
          const submittedSession = await proposalApi.getSession(activeSessionId);
          if ((submittedSession.status || '').toLowerCase() !== 'open') {
            setLastAgentSession(submittedSession);
          }
        } else {
          // Legacy synchronous response (if any)
          setMessage(
            `Chunk analysis submitted. Foundry invoked: ${res.foundry_invoked ? 'yes' : 'no'}.`
          );
          await waitForSubmission(activeSessionId);
          const submittedSession = await proposalApi.getSession(activeSessionId);
          if ((submittedSession.status || '').toLowerCase() !== 'open') {
            setLastAgentSession(submittedSession);
          }
        }

        // Refresh document list to show updated chunk status
        if (selectedIndex) {
          await loadDocuments(selectedIndex, true);
        }
        if (responseStatus === 'no_changes') {
          await loadLastAgentSession();
        }
      } catch (err) {
        const errorMsg = (err as Error).message || 'Unknown error';
        // Check if it's a network/timeout error
        if (errorMsg.includes('timeout') || errorMsg.includes('deadline')) {
          setMessage(
            '⚠ Request timeout. The analysis took too long. Try again or check if the service is available.'
          );
        } else if (errorMsg.includes('Network') || errorMsg.includes('Failed to fetch')) {
          setMessage('⚠ Network error. Check your connection and try again.');
        } else {
          setMessage(`⚠ Failed to analyze chunk: ${errorMsg}`);
        }
        onSessionSubmitted?.();
        await loadLastAgentSession();
      } finally {
        setAnalyzingChunkIds((current) => {
          const next = new Set(current);
          next.delete(chunk.id);
          return next;
        });
      }
    };

    const resetChunk = async (
      doc: CurationDocument,
      chunk: CurationChunk,
      clearRejections: boolean
    ): Promise<void> => {
      const normalizedStatus = (chunk.evaluation_status || '').trim().toLowerCase();
      if (
        normalizedStatus !== 'approved' &&
        normalizedStatus !== 'rejected' &&
        normalizedStatus !== 'pending' &&
        normalizedStatus !== 'processed'
      ) {
        setMessage('Only processed, pending, or rejected chunks can be reset.');
        return;
      }

      setResettingChunkId(chunk.id);
      setMessage('');
      try {
        await curationApi.resetChunk(doc.index_name, doc.id, chunk.id, { clearRejections });
        if (selectedIndex) {
          const settled = await waitForChunkReset(selectedIndex, doc.id, chunk.id);
          setMessage(
            settled
              ? `Chunk ${chunk.id} reset.${clearRejections ? '' : ' Rejections were preserved.'}`
              : `Chunk ${chunk.id} reset, but the refreshed status is still settling. Try refresh if it does not update shortly.`
          );
        } else {
          setMessage(
            `Chunk ${chunk.id} reset.${clearRejections ? '' : ' Rejections were preserved.'}`
          );
        }
      } catch (err) {
        setMessage(`Failed to reset chunk: ${(err as Error).message}`);
      } finally {
        setResettingChunkId('');
      }
    };

    const resetPreferenceKey = (documentId: string, chunkId: string): string => {
      return `${documentId}::${chunkId}`;
    };

    const shouldClearRejectionsOnReset = (documentId: string, chunkId: string): boolean => {
      return resetClearRejectionsByChunk[resetPreferenceKey(documentId, chunkId)] ?? true;
    };

    const setChunkResetClearRejections = (
      documentId: string,
      chunkId: string,
      clearRejections: boolean
    ): void => {
      setResetClearRejectionsByChunk((prev) => ({
        ...prev,
        [resetPreferenceKey(documentId, chunkId)]: clearRejections,
      }));
    };

    const addFreeformDocument = async (): Promise<void> => {
      if (!selectedIndexAllowsUploads) {
        setMessage('Uploads are disabled for the selected index.');
        return;
      }
      if (!selectedIndex) {
        setMessage('Select an index before adding user content.');
        return;
      }
      const trimmedTitle = freeformTitle.trim();
      const trimmed = freeformContent.trim();
      if (!trimmedTitle) {
        setMessage('Enter a freeform title.');
        return;
      }
      if (!trimmed) {
        setMessage('Enter freeform content to add.');
        return;
      }

      setAddingFreeform(true);
      setMessage('');
      try {
        const res = await curationApi.addDocument(selectedIndex, {
          title: trimmedTitle,
          content: trimmed,
        });
        setMessage(`Freeform content added to ${res.index_name}.`);
        setFreeformTitle('');
        setFreeformContent('');
        setShowFreeformForm(false);

        await new Promise((resolve) => setTimeout(resolve, 1500));
        await loadDocuments(selectedIndex, true);
      } catch (err) {
        setMessage(`Failed to add freeform content: ${(err as Error).message}`);
      } finally {
        setAddingFreeform(false);
      }
    };

    const prefillFreeformFromFile = useCallback(async (file: File): Promise<void> => {
      if (!isAcceptedTextFile(file)) {
        setMessage(
          'Only plain text files are supported. Drop a text-based file such as .txt, .md, .csv, .json, or source code.'
        );
        return;
      }

      try {
        const content = await file.text();
        if (content.trim() === '') {
          setMessage('The dropped file is empty.');
          return;
        }

        setFreeformTitle(getDefaultTitleFromFileName(file.name));
        setFreeformContent(content);
        setShowFreeformForm(true);
        setMessage(`Loaded ${file.name}. Review and submit when ready.`);
      } catch (err) {
        setMessage(`Failed to read dropped file: ${(err as Error).message}`);
      }
    }, []);

    const handleFreeformDragEnter = (event: React.DragEvent<HTMLElement>): void => {
      if (!selectedIndexAllowsUploads) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      freeformDragDepthRef.current += 1;
      setIsFreeformDropActive(true);
    };

    const handleFreeformDragOver = (event: React.DragEvent<HTMLElement>): void => {
      if (!selectedIndexAllowsUploads) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      if (!isFreeformDropActive) {
        setIsFreeformDropActive(true);
      }
    };

    const handleFreeformDragLeave = (event: React.DragEvent<HTMLElement>): void => {
      if (!selectedIndexAllowsUploads) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      freeformDragDepthRef.current = Math.max(0, freeformDragDepthRef.current - 1);
      if (freeformDragDepthRef.current === 0) {
        setIsFreeformDropActive(false);
      }
    };

    const handleFreeformDrop = async (event: React.DragEvent<HTMLElement>): Promise<void> => {
      if (!selectedIndexAllowsUploads) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      freeformDragDepthRef.current = 0;
      setIsFreeformDropActive(false);

      if (addingFreeform || resettingChunkId !== '') {
        return;
      }

      const files = Array.from(event.dataTransfer.files ?? []);
      if (files.length === 0) {
        setMessage('Drop a text file to prefill user content.');
        return;
      }
      if (files.length > 1) {
        setMessage('Drop a single text file at a time.');
        return;
      }

      await prefillFreeformFromFile(files[0]);
    };

    useEffect(() => {
      if (selectedIndexAllowsUploads) {
        return;
      }
      setShowFreeformForm(false);
      setIsFreeformDropActive(false);
      freeformDragDepthRef.current = 0;
    }, [selectedIndexAllowsUploads]);

    const toggleExpanded = (documentID: string): void => {
      setExpandedDocumentIds((prev) => ({
        ...prev,
        [documentID]: !prev[documentID],
      }));
    };

    const handleToggleExpanded = (
      event: React.MouseEvent<HTMLButtonElement>,
      documentID: string
    ): void => {
      event.preventDefault();
      event.stopPropagation();
      toggleExpanded(documentID);
    };

    const deleteDocument = async (doc: CurationDocument): Promise<void> => {
      const label = doc.title || doc.filename || doc.id;
      if (!window.confirm(`Delete "${label}" and all its chunks from this index?`)) return;
      setMessage('');
      try {
        await curationApi.deleteDocument(doc.index_name || selectedIndex, doc.id);
        setMessage(`Deleted "${label}".`);
        // Remove from local state — no refetch needed since the API confirmed deletion
        // (immediate refetch can return stale data from the search index, undoing the removal)
        setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
        setExpandedDocumentIds((prev) => {
          const next = { ...prev };
          delete next[doc.id];
          return next;
        });
      } catch (err) {
        setMessage(`Failed to delete document: ${(err as Error).message}`);
      }
    };

    const statusClass = (status?: string): string => {
      const normalized = (status || 'unprocessed').toLowerCase();
      if (normalized === 'processing') return 'is-processing';
      if (normalized === 'processed') return 'is-processed';
      if (normalized === 'rejected') return 'is-rejected';
      if (normalized === 'mixed') return 'is-mixed';
      return 'is-unprocessed';
    };

    const normalizeProcessState = (status?: string, evaluationStatus?: string): string => {
      const normalized = (status || evaluationStatus || 'unprocessed').trim().toLowerCase();
      return normalized || 'unprocessed';
    };

    const OPEN_BY_DEFAULT_STATES = ['unprocessed', 'processing', 'rejected'];

    const allProcessStates = useMemo<string[]>(() => {
      const preferredOrder = ['unprocessed', 'processing', 'rejected', 'processed', 'proposed', 'mixed'];
      const stateSet = new Set<string>();

      documents.forEach((doc) => {
        stateSet.add(normalizeProcessState(doc.status, doc.evaluation_status));
      });

      return Array.from(stateSet).sort((a, b) => {
        const left = preferredOrder.indexOf(a);
        const right = preferredOrder.indexOf(b);
        if (left >= 0 && right >= 0) return left - right;
        if (left >= 0) return -1;
        if (right >= 0) return 1;
        return a.localeCompare(b);
      });
    }, [documents]);

    const documentsByState = useMemo<Record<string, CurationDocument[]>>(() => {
      const bucket: Record<string, CurationDocument[]> = {};
      documents.forEach((doc) => {
        const state = normalizeProcessState(doc.status, doc.evaluation_status);
        if (!bucket[state]) bucket[state] = [];
        bucket[state].push(doc);
      });
      return bucket;
    }, [documents]);

    useEffect(() => {
      setExpandedStatuses((prev) => {
        let changed = false;
        const next: Record<string, boolean> = { ...prev };
        allProcessStates.forEach((state) => {
          if (!(state in next)) {
            next[state] = OPEN_BY_DEFAULT_STATES.includes(state);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, [allProcessStates]);

    const toggleStatusSection = (state: string): void => {
      setExpandedStatuses((prev) => ({ ...prev, [state]: !prev[state] }));
    };

    const sanitizeChunkContent = (content?: string): string => {
      if (!content) return '';
      return content.replace(/\bTEST\b/g, '').replace(/ {2,}/g, ' ');
    };

    const isFocusedChunk = (documentID: string, chunk: CurationChunk): boolean => {
      if (!focusedDocumentID || !focusedChunkToken) return false;
      if (focusedDocumentID !== documentID) return false;
      return chunk.id === focusedChunkToken;
    };

    const handleFocusChunk = useCallback((documentID: string, chunkID: string): void => {
      setFocusedDocumentID(documentID);
      setFocusedChunkToken(chunkID);
    }, []);

    const handleChunkCardClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>, documentID: string, chunkID: string): void => {
        // Ignore clicks that originate on interactive descendants (buttons, inputs,
        // links) so the card focus doesn't fight with per-chunk actions.
        const target = event.target as HTMLElement | null;
        if (target && target.closest('button, a, input, label, textarea, select')) {
          return;
        }
        handleFocusChunk(documentID, chunkID);
      },
      [handleFocusChunk]
    );

    const getChunkSessionIds = useCallback((chunk: CurationChunk): string[] => {
      const details = chunk.evaluation_details ?? [];
      const seen = new Set<string>();
      const sessionIds: string[] = [];
      details.forEach((detail) => {
        const sessionId = detail.session_id?.trim();
        if (!sessionId || seen.has(sessionId)) {
          return;
        }
        seen.add(sessionId);
        sessionIds.push(sessionId);
      });
      return sessionIds;
    }, []);

    const getChunkRejections = useCallback(
      (documentID: string, chunk: CurationChunk): RejectionRecord[] => {
        const all = rejectionHistoryByDocument[documentID] ?? [];
        const sessions = new Set(getChunkSessionIds(chunk));
        if (sessions.size === 0) return [];
        return all.filter((item) => {
          if (!sessions.has(item.session_id)) return false;
          const sourceChunkID = item.source_chunk_id?.trim();
          return sourceChunkID === '' || sourceChunkID === undefined || sourceChunkID === chunk.id;
        });
      },
      [getChunkSessionIds, rejectionHistoryByDocument]
    );

    const getChunkJustifications = useCallback(
      (documentID: string, chunk: CurationChunk): JustificationRecord[] => {
        const all = justificationHistoryByDocument[documentID] ?? [];
        const sessions = new Set(getChunkSessionIds(chunk));
        if (sessions.size === 0) return [];
        return all.filter((item) => {
          if (!sessions.has(item.session_id)) return false;
          const sourceChunkID = item.source_chunk_id?.trim();
          return sourceChunkID === '' || sourceChunkID === undefined || sourceChunkID === chunk.id;
        });
      },
      [getChunkSessionIds, justificationHistoryByDocument]
    );

    const getSessionDetail = (
      chunk: CurationChunk,
      sessionID: string
    ): { status?: string; evaluated_at?: string } | null => {
      const details = chunk.evaluation_details ?? [];
      return details.find((detail) => detail.session_id === sessionID) ?? null;
    };

    const getSessionBadgeStatus = (chunk: CurationChunk, sessionID: string): string => {
      const detailStatus = getSessionDetail(chunk, sessionID)?.status;
      if (detailStatus) {
        return detailStatus;
      }
      if (chunk.evaluation_status === 'processed') {
        return 'approved';
      }
      return chunk.evaluation_status || 'pending';
    };

    const getSessionBadgeTitle = (chunk: CurationChunk, sessionID: string): string => {
      const detail = getSessionDetail(chunk, sessionID);
      const badgeStatus = getSessionBadgeStatus(chunk, sessionID);
      if (detail?.evaluated_at) {
        return `${badgeStatus} @ ${detail.evaluated_at} — click to view`;
      }
      return `${badgeStatus} — click to view`;
    };

    const renderDocCard = (doc: CurationDocument): React.ReactElement => (
      <li
        key={`${doc.run_id || 'doc'}:${doc.id}`}
        id={`doc-card-${doc.id}`}
        className={`content-panel__doc-card${focusedDocumentID === doc.id ? ' content-panel__doc-card--focused' : ''}`}
      >
        <div className="content-panel__doc-head">
          <div className="content-panel__doc-title-wrap">
            <span className="content-panel__doc-title">
              {doc.title || doc.filename || doc.id}
            </span>
          </div>
          <div className="content-panel__doc-actions">
            <button
              type="button"
              className="content-panel__toggle-btn"
              onClick={(event) => handleToggleExpanded(event, doc.id)}
              disabled={resettingChunkId !== '' || addingFreeform}
              aria-expanded={Boolean(expandedDocumentIds[doc.id])}
              aria-controls={`doc-details-${doc.id}`}
              aria-label={`${expandedDocumentIds[doc.id] ? 'Collapse' : 'Expand'} ${doc.title || doc.filename || doc.id}`}
              title={expandedDocumentIds[doc.id] ? 'Collapse document details' : 'Expand document details'}
            >
              <i className="modus-icons" aria-hidden="true">
                {expandedDocumentIds[doc.id] ? 'expand_less' : 'expand_more'}
              </i>
            </button>
          </div>
        </div>
        {expandedDocumentIds[doc.id] && (
          <>
            <div className="content-panel__doc-details-head">
              <div className="content-panel__doc-details-meta">
                <span className={`content-panel__doc-badge ${statusClass(doc.status)}`}>
                  {(doc.status || 'unprocessed').toLowerCase()}
                </span>
                {doc.uri && <span className="content-panel__doc-uri">{doc.uri}</span>}
              </div>
              <div className="content-panel__doc-actions content-panel__doc-actions--body">
                <button
                  type="button"
                  className="content-panel__delete-btn"
                  onClick={() => void deleteDocument(doc)}
                  disabled={resettingChunkId !== '' || addingFreeform}
                  aria-label={`Delete ${doc.title || doc.filename || doc.id}`}
                  title="Delete document"
                >
                  <i className="modus-icons" aria-hidden="true">
                    delete
                  </i>
                </button>
              </div>
            </div>
            <dl id={`doc-details-${doc.id}`} className="content-panel__doc-meta">
              <div>
                <dt>ID</dt>
                <dd>{doc.id}</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>{doc.source || 'n/a'}</dd>
              </div>
            </dl>
            <div
              className="content-panel__chunk-list"
              role="list"
              aria-label={`Chunks for ${doc.title || doc.filename || doc.id}`}
            >
              {(doc.chunks ?? []).length === 0 ? (
                <p className="content-panel__chunk-empty">
                  No chunks available for this document.
                </p>
              ) : (
                doc.chunks?.map((chunk) => (
                  <div
                    key={chunk.id}
                    className={`content-panel__chunk-card${isFocusedChunk(doc.id, chunk) ? ' content-panel__chunk-card--focused' : ''}`}
                    role="listitem"
                    data-doc-id={doc.id}
                    data-chunk-id={chunk.id}
                    onClick={(event) => handleChunkCardClick(event, doc.id, chunk.id)}
                  >
                    <div className="content-panel__chunk-head">
                      <div className="content-panel__chunk-title-wrap">
                        <code className="content-panel__chunk-id">{chunk.id}</code>
                        <span
                          className={`content-panel__doc-badge ${statusClass(chunk.evaluation_status === 'approved' ? 'processed' : chunk.evaluation_status)}`}
                        >
                          {(chunk.evaluation_status === 'approved'
                            ? 'processed'
                            : chunk.evaluation_status || 'unprocessed'
                          ).toLowerCase()}
                        </span>
                      </div>
                      <div className="content-panel__chunk-actions">
                        {((chunk.evaluation_status || 'unprocessed').toLowerCase() === 'unprocessed' ||
                          (chunk.evaluation_status || '').toLowerCase() === 'rejected') && (
                          <button
                            type="button"
                            className="content-panel__chunk-process-btn"
                            onClick={() => void analyzeChunk(doc, chunk)}
                            disabled={
                              addingFreeform ||
                              analyzingChunkIds.has(chunk.id) ||
                              resettingChunkId !== ''
                            }
                            aria-label={`Process chunk ${chunk.id}`}
                            title={
                              analyzingChunkIds.has(chunk.id)
                                ? 'Processing chunk...'
                                : 'Process chunk'
                            }
                          >
                            <i className="modus-icons" aria-hidden="true">
                              refresh
                            </i>
                          </button>
                        )}
                        {(() => {
                          const normalized = (chunk.evaluation_status || '').toLowerCase();
                          const resettable = new Set([
                            'approved',
                            'processed',
                            'pending',
                            'proposed',
                            'processing',
                          ]);
                          if (!resettable.has(normalized)) return null;
                          const stuck = normalized === 'pending' || normalized === 'proposed' || normalized === 'processing';
                          return (
                            <button
                              type="button"
                              className="content-panel__secondary-btn"
                              onClick={() =>
                                void resetChunk(
                                  doc,
                                  chunk,
                                  shouldClearRejectionsOnReset(doc.id, chunk.id)
                                )
                              }
                              disabled={addingFreeform || resettingChunkId !== ''}
                              title={stuck ? 'Reset stuck chunk back to unprocessed' : 'Reset chunk to unprocessed'}
                            >
                              {resettingChunkId === chunk.id ? 'Resetting...' : 'Reset'}
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                    <dl className="content-panel__chunk-meta">
                      <div>
                        <dt>Sessions</dt>
                        <dd>
                          {getChunkSessionIds(chunk).length
                            ? getChunkSessionIds(chunk).map((sid) => {
                                const badgeStatus = getSessionBadgeStatus(chunk, sid);
                                return (
                                  <button
                                    key={sid}
                                    type="button"
                                    className={`content-panel__session-badge content-panel__session-badge--${badgeStatus}`}
                                    onClick={() => onSelectSession?.(sid)}
                                    title={getSessionBadgeTitle(chunk, sid)}
                                  >
                                    {sid.length > 8 ? `…${sid.slice(-8)}` : sid}
                                  </button>
                                );
                              })
                            : 'n/a'}
                        </dd>
                      </div>
                      <div>
                        <dt>Updated</dt>
                        <dd>{chunk.updated_at || 'n/a'}</dd>
                      </div>
                    </dl>
                    {chunk.content && (
                      <div className="content-panel__doc-content content-panel__doc-content--chunk">
                        <h4>Text</h4>
                        <pre>{sanitizeChunkContent(chunk.content)}</pre>
                      </div>
                    )}
                    {getChunkJustifications(doc.id, chunk).length > 0 && (
                      <div
                        className="content-panel__justification-history"
                        aria-label="Justification history"
                      >
                        <h4>Justification History</h4>
                        <ul>
                          {getChunkJustifications(doc.id, chunk).map((item, idx) => (
                            <li key={`${item.session_id}-${item.proposal_step_id}-${idx}`}>
                              <div className="content-panel__justification-text">
                                {item.justification || 'No justification provided'}
                              </div>
                              {(item.approved_by || item.submitted_by || item.step_summary) && (
                                <div className="content-panel__rejection-meta">
                                  {item.approved_by
                                    ? `Approved by ${item.approved_by}`
                                    : item.submitted_by
                                      ? `Submitted by ${item.submitted_by}`
                                      : ''}
                                  {item.step_summary ? ` · ${item.step_summary}` : ''}
                                </div>
                              )}
                              {item.node_id && (
                                <div className="content-panel__justification-nodes">
                                  Node: {item.node_id}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {getChunkRejections(doc.id, chunk).length > 0 && (
                      <div
                        className="content-panel__rejection-history"
                        aria-label="Rejection history"
                      >
                        <h4>Rejection History</h4>
                        <ul>
                          {getChunkRejections(doc.id, chunk).map((item, idx) => (
                            <li key={`${item.session_id}-${idx}`}>
                              <div className="content-panel__rejection-reason">
                                {item.reason || 'No reason provided'}
                              </div>
                              <div className="content-panel__rejection-meta">
                                <button
                                  type="button"
                                  className="content-panel__session-link"
                                  onClick={() => onSelectSession?.(item.session_id)}
                                  title="Jump to session in Review panel"
                                >
                                  Open Session
                                </button>
                                {item.step_summary ? ' · ' + item.step_summary : ''}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </li>
    );

    return (
      <div className="content-panel">
        <div className="content-panel__header">
          <div className="content-panel__storyline" aria-label="Document view narrative">
            <span>Documents in</span>
            <select
              id="content-index-select"
              value={selectedIndex}
              onChange={(e) => setSelectedIndex(e.target.value)}
              disabled={
                loadingIndexes ||
                addingFreeform ||
                resettingChunkId !== '' ||
                indexes.length === 0
              }
              aria-label="Select index"
              className="content-panel__story-select"
            >
              {indexes.map((idx) => (
                <option key={idx.name} value={idx.name}>
                  {idx.name}
                </option>
              ))}
            </select>
            <span>to branch</span>
            <strong className="content-panel__story-session">
              {lastAgentSession?.title || 'Live Graph'}
            </strong>
          </div>
          {selectedIndexAllowsUploads && (
            <div
              className={`content-panel__freeform-launcher ${isFreeformDropActive ? 'content-panel__freeform-launcher--drop-active' : ''}`}
              onDragEnter={handleFreeformDragEnter}
              onDragOver={handleFreeformDragOver}
              onDragLeave={handleFreeformDragLeave}
              onDrop={(event) => {
                void handleFreeformDrop(event);
              }}
            >
              <button
                type="button"
                className={`content-panel__secondary-btn ${isFreeformDropActive ? 'content-panel__secondary-btn--drop-active' : ''}`}
                onClick={() => setShowFreeformForm((prev) => !prev)}
                disabled={
                  loadingDocuments ||
                  resettingChunkId !== '' ||
                  addingFreeform
                }
                title="Click to add user content, or drop a text file here to prefill the form"
              >
                {showFreeformForm ? 'Cancel' : 'Add User Content'}
              </button>
              <div className="content-panel__freeform-hint" aria-live="polite">
                <span>
                  {isFreeformDropActive ? 'Release to prefill.' : 'Drop a text file here.'}
                </span>
              </div>
              {showFreeformForm && (
                <div
                  className="content-panel__freeform-popup"
                  role="dialog"
                  aria-label="Add User Content"
                >
                  <label htmlFor="freeform-title">Title</label>
                  <input
                    id="freeform-title"
                    type="text"
                    value={freeformTitle}
                    onChange={(e) => setFreeformTitle(e.target.value)}
                    placeholder="Required title"
                    disabled={addingFreeform || resettingChunkId !== ''}
                  />
                  <label htmlFor="freeform-content">Content</label>
                  <textarea
                    id="freeform-content"
                    value={freeformContent}
                    onChange={(e) => setFreeformContent(e.target.value)}
                    placeholder="Paste capability notes, roadmap text, or curated snippets here."
                    rows={4}
                    disabled={addingFreeform || resettingChunkId !== ''}
                  />
                  <button
                    type="button"
                    onClick={addFreeformDocument}
                    disabled={
                      addingFreeform ||
                      resettingChunkId !== '' ||
                      freeformTitle.trim() === '' ||
                      freeformContent.trim() === ''
                    }
                  >
                    {addingFreeform ? 'Adding...' : 'Submit'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="content-panel__columns">
          <section className="content-panel__section content-panel__section--left">
            {loadingDocuments ? (
              <p>Loading documents...</p>
            ) : documents.length === 0 ? (
              <p>No documents in this index.</p>
            ) : (
              allProcessStates
                .filter((state) => (documentsByState[state]?.length ?? 0) > 0)
                .map((state) => {
                  const expanded = Boolean(expandedStatuses[state]);
                  const docsForState = documentsByState[state];
                  return (
                    <section key={state} className="content-panel__status-section">
                      <button
                        type="button"
                        className="content-panel__status-section-toggle"
                        onClick={() => toggleStatusSection(state)}
                        aria-expanded={expanded}
                        aria-controls={`status-section-${state}`}
                      >
                        <i className="modus-icons" aria-hidden="true">
                          {expanded ? 'expand_less' : 'expand_more'}
                        </i>
                        <h3 className="content-panel__section-heading">
                          {state} ({docsForState.length})
                        </h3>
                      </button>
                      {expanded && (
                        <ul
                          id={`status-section-${state}`}
                          className="content-panel__doc-cards"
                          aria-label={`${state} document cards`}
                        >
                          {docsForState.map(renderDocCard)}
                        </ul>
                      )}
                    </section>
                  );
                })
            )}
            <p className="content-panel__doc-count">
              {documents.length} document{documents.length === 1 ? '' : 's'}
            </p>
          </section>
        </div>
        {message && <p className="content-panel__message">{message}</p>}
      </div>
    );
  }
);

ContentPanel.displayName = 'ContentPanel';

export default ContentPanel;
